import axios, { type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { newRequestId } from '../utils/requestId';
import { ApiError, type ApiEnvelope, type ApiErrorEnvelope } from './types';

export type ApiClientConfig = {
  baseURL: string;
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  onTokensRefreshed: (tokens: {
    access_token: string;
    refresh_token: string;
    expires_at: string;
    refresh_expires_at: string | null;
  }) => void;
  onSessionExpired: () => void;
};

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshInFlight: Promise<string> | null = null;
let pendingQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

function flushQueue(error: unknown, token: string | null): void {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error || !token) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
}

export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const client = axios.create({
    baseURL: config.baseURL,
    timeout: 30_000,
    headers: { Accept: 'application/json' },
    validateStatus: () => true,
  });

  client.interceptors.request.use((req) => {
    req.headers.set('X-Request-Id', newRequestId());
    const token = config.getAccessToken();
    if (token && !req.headers.has('Authorization')) {
      req.headers.set('Authorization', `Bearer ${token}`);
    }
    return req;
  });

  client.interceptors.response.use(async (response) => {
    const originalRequest = response.config as RetryableConfig;

    if (response.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
      originalRequest._retry = true;
      try {
        const newToken = await ensureRefresh(config);
        originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
        return client.request(originalRequest);
      } catch {
        config.onSessionExpired();
        throw new ApiError({
          message: 'Session expired. Please sign in again.',
          httpStatus: 401,
        });
      }
    }

    if (response.status >= 400) {
      const body = response.data as ApiErrorEnvelope;
      throw new ApiError({
        message: body?.message ?? 'Request failed.',
        httpStatus: response.status,
        errors: body?.errors,
      });
    }

    return response;
  });

  return client;
}

async function ensureRefresh(config: ApiClientConfig): Promise<string> {
  if (refreshInFlight) {
    return new Promise<string>((resolve, reject) => {
      pendingQueue.push({ resolve, reject });
    });
  }

  refreshInFlight = (async () => {
    const refreshToken = config.getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token');

    const response = await axios.post<ApiEnvelope<{
      access_token: string;
      refresh_token: string;
      expires_at: string;
      refresh_expires_at: string | null;
    }>>(
      `${config.baseURL}/auth/refresh`,
      { refresh_token: refreshToken },
      {
        headers: { Accept: 'application/json', 'X-Request-Id': newRequestId() },
        validateStatus: () => true,
      },
    );

    if (response.status >= 400 || !response.data?.data) {
      throw new Error('Refresh failed');
    }

    const data = response.data.data;
    config.onTokensRefreshed({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at,
      refresh_expires_at: data.refresh_expires_at,
    });
    return data.access_token;
  })();

  try {
    const token = await refreshInFlight;
    flushQueue(null, token);
    return token;
  } catch (err) {
    flushQueue(err, null);
    throw err;
  } finally {
    refreshInFlight = null;
  }
}

export async function unwrap<T>(p: Promise<AxiosResponse<ApiEnvelope<T>>>): Promise<T> {
  const response = await p;
  return response.data.data;
}

export async function unwrapList<T>(p: Promise<AxiosResponse<ApiEnvelope<T[] | { data: T[] }>>>): Promise<T[]> {
  const response = await p;
  const data = response.data.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as { data: T[] }).data)) {
    return (data as { data: T[] }).data;
  }
  return [];
}

export const ApiClientKey = Symbol('ApiClient');
